import React, { useState } from 'react';

import {
  Badge,
  Box,
  Button,
  ButtonGroup,
  Divider,
  HStack,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Switch,
  Text,
  Tooltip,
  VStack,
  useDisclosure,
} from '@chakra-ui/react';
import {
  type SubmitHandler,
  useFieldArray,
  useForm,
  useFormContext,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { LuPlus } from 'react-icons/lu';

import { Form, FormField } from '@/components';
import { Icon } from '@/components/Icons';
import {
  CheckboxRadio,
  IconAlertCircle,
  IconSettings,
  Stroke,
} from '@/components/Icons/icons-generated/';
import { InterviewType } from '@/services/api';

import {
  AiQuestionsGenerate,
  LanguageSelectionMenu,
} from './AiQuestionsGenerate';
import { QuestionCardList } from './QuestionCardList';
import { interviewTypes } from './StepInterviewInformation';
import {
  type NewInterviewFormInputs,
  type NewInterviewFormStepProps,
  defaultQuestion,
} from './common';

export type InterviewSettingsModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSubmit?: (data: InterviewSettingsForm) => void;
  initialValues?: Partial<InterviewSettingsForm>;
  isEditable?: boolean;
};

export type InterviewSettingsForm = {
  title: string;
  type: InterviewType;
  isShuffleEnabled?: boolean;
  isCamRecordingEnabled?: boolean;
  isShowGuideEnabled?: boolean;
  isEmotionAnalysisEnabled?: boolean;
  isSuggestionEnabled?: boolean;
  isAiAvatarEnabled?: boolean;
  isSecurityMeasuresEnabled?: boolean;
};

export const InterviewSettingsModal: React.FC<InterviewSettingsModalProps> = (
  props
) => {
  const { t } = useTranslation(['common', 'interview']);
  const { isOpen, onClose, onSubmit, initialValues, isEditable } = props;

  const form = useForm<InterviewSettingsForm>({
    mode: 'onBlur',
    defaultValues: {
      isCamRecordingEnabled: initialValues?.isCamRecordingEnabled,
      isShuffleEnabled: initialValues?.isShuffleEnabled,
      isShowGuideEnabled: initialValues?.isShowGuideEnabled,
      isEmotionAnalysisEnabled: initialValues?.isEmotionAnalysisEnabled,
      isSuggestionEnabled: initialValues?.isSuggestionEnabled,
      isAiAvatarEnabled: initialValues?.isAiAvatarEnabled,
      title: initialValues?.title ?? '',
      type: initialValues?.type ?? InterviewType.JobInterview,
      isSecurityMeasuresEnabled: initialValues?.isSecurityMeasuresEnabled,
    },
  });

  const isCamRecordingEnabled = form.watch('isCamRecordingEnabled');
  const isEmotionAnalysisEnabled = form.watch('isEmotionAnalysisEnabled');

  const handleSubmit: SubmitHandler<InterviewSettingsForm> = async (data) => {
    if (onSubmit) {
      onSubmit(data);
    }
    onClose();
  };

  return (
    <Modal isCentered isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent borderRadius="2xl">
        <ModalCloseButton
          borderRadius="full"
          borderWidth="1px"
          borderColor="gray.100"
          mt={1.5}
        />
        <ModalHeader borderBottomWidth={1} borderColor="gray.50">
          <Text textAlign="center">{t('common:generalSettings')}</Text>
        </ModalHeader>
        <Form {...form}>
          <ModalBody>
            <VStack>
              <FormField
                name="title"
                type="text"
                rules={{
                  required: t(
                    'interview:createNewInterview.interviewInformation.interviewTitleRequired'
                  ),
                  validate: (value) => {
                    if (value && value?.trim().length < 3) {
                      return t(
                        'interview:createNewInterview.interviewInformation.interviewTitleMinLength'
                      );
                    }
                  },
                }}
                label={t(
                  'interview:createNewInterview.interviewInformation.interviewTitle'
                )}
              />
              <FormField
                type="select"
                optionalityHint="required"
                options={interviewTypes}
                isDisabled={!isEditable}
                label={t(
                  'interview:createNewInterview.questionPreparation.interviewType'
                )}
                rules={{
                  required: t(
                    'interview:createNewInterview.questionPreparation.interviewTypeRequired'
                  ),
                }}
                placeholder={t(
                  'interview:createNewInterview.questionPreparation.selectInterviewType'
                )}
                name="type"
              />
              <VStack spacing={3} mt={2} alignItems="start" w="full">
                <FormField
                  type="custom"
                  name="isShuffleEnabled"
                  render={({ field: { value, ...rest } }) => {
                    return (
                      <HStack>
                        <Switch
                          isChecked={value}
                          defaultChecked={value}
                          colorScheme="brand"
                          color="red.500"
                          size="md"
                          isDisabled={!isEditable}
                          {...rest}
                        />
                        <Text fontSize={14}>
                          {t(
                            'interview:createNewInterview.questionPreparation.enableShuffle'
                          )}
                        </Text>
                      </HStack>
                    );
                  }}
                />
                <FormField
                  type="custom"
                  name="isCamRecordingEnabled"
                  render={({ field: { value, onChange, ...rest } }) => {
                    const handleCamRecordingChange = (
                      event: React.ChangeEvent<HTMLInputElement>
                    ) => {
                      const checked = event.target.checked;
                      if (!checked && isEmotionAnalysisEnabled) {
                        form.setValue('isEmotionAnalysisEnabled', false);
                      }
                      onChange(event);
                    };
                    return (
                      <HStack>
                        <Switch
                          isChecked={value}
                          defaultChecked={value}
                          colorScheme="brand"
                          color="red.500"
                          size="md"
                          isDisabled={!isEditable}
                          onChange={handleCamRecordingChange}
                          {...rest}
                        />
                        <Text fontSize={14}>
                          {t(
                            'interview:createNewInterview.questionPreparation.isCameraRecording'
                          )}
                        </Text>
                        <Tooltip
                          hasArrow
                          textAlign="center"
                          maxW="250px"
                          label={t(
                            'interview:createNewInterview.questionPreparation.isCameraRecordingNote'
                          )}
                          bg="black"
                          color="white"
                          borderRadius="lg"
                          px={3}
                          py={2}
                        >
                          <IconAlertCircle fontSize={20} color="gray.500" />
                        </Tooltip>
                      </HStack>
                    );
                  }}
                />
                <FormField
                  type="custom"
                  name="isEmotionAnalysisEnabled"
                  render={({ field: { value, onChange, ...rest } }) => {
                    const handleEmotionAnalysisChange = (
                      event: React.ChangeEvent<HTMLInputElement>
                    ) => {
                      const checked = event.target.checked;
                      if (checked && !isCamRecordingEnabled) {
                        form.setValue('isCamRecordingEnabled', true);
                      }
                      onChange(event);
                    };

                    return (
                      <HStack>
                        <Switch
                          isChecked={value}
                          defaultChecked={value}
                          colorScheme="brand"
                          color="red.500"
                          size="md"
                          isDisabled={!isEditable}
                          onChange={handleEmotionAnalysisChange}
                          {...rest}
                        />
                        <Text fontSize={14}>
                          {t(
                            'interview:createNewInterview.questionPreparation.emotionAnalysis'
                          )}
                        </Text>
                      </HStack>
                    );
                  }}
                />
                <FormField
                  type="custom"
                  name="isShowGuideEnabled"
                  render={({ field: { value, ...rest } }) => {
                    return (
                      <HStack>
                        <Switch
                          isChecked={value}
                          defaultChecked={value}
                          colorScheme="brand"
                          color="red.500"
                          size="md"
                          isDisabled={!isEditable}
                          {...rest}
                        />
                        <Text fontSize={14}>
                          {t(
                            'interview:createNewInterview.questionPreparation.isShowGuideEnabled'
                          )}
                        </Text>
                      </HStack>
                    );
                  }}
                />
                <FormField
                  type="custom"
                  name="isSuggestionEnabled"
                  render={({ field: { value, ...rest } }) => {
                    return (
                      <HStack>
                        <Switch
                          isChecked={value}
                          defaultChecked={value}
                          colorScheme="brand"
                          color="red.500"
                          size="md"
                          isDisabled={!isEditable}
                          {...rest}
                        />
                        <Text fontSize={14}>
                          {t(
                            'interview:createNewInterview.questionPreparation.aiSuggestion'
                          )}
                        </Text>
                      </HStack>
                    );
                  }}
                />
                <FormField
                  type="custom"
                  name="isAiAvatarEnabled"
                  render={({ field: { value, ...rest } }) => {
                    return (
                      <HStack>
                        <Switch
                          isChecked={value}
                          defaultChecked={value}
                          colorScheme="brand"
                          color="red.500"
                          size="md"
                          isDisabled={!isEditable}
                          {...rest}
                        />
                        <Text fontSize={14}>
                          {t(
                            'interview:createNewInterview.questionPreparation.aiAvatar'
                          )}
                        </Text>
                      </HStack>
                    );
                  }}
                />
                <FormField
                  type="custom"
                  name="isSecurityMeasuresEnabled"
                  render={({ field: { value, ...rest } }) => {
                    return (
                      <HStack>
                        <Switch
                          isChecked={value}
                          defaultChecked={value}
                          colorScheme="brand"
                          color="red.500"
                          size="md"
                          isDisabled={!isEditable}
                          {...rest}
                        />
                        <Text fontSize={14}>
                          {t(
                            'interview:createNewInterview.questionPreparation.securityMeasures'
                          )}
                        </Text>
                      </HStack>
                    );
                  }}
                />
              </VStack>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <HStack gap={3} w="full">
              <Button
                flexGrow={1}
                variant="@primary"
                onClick={() => {
                  form.handleSubmit(handleSubmit)();
                }}
              >
                {t('common:actions.save')}
              </Button>
            </HStack>
          </ModalFooter>
        </Form>
      </ModalContent>
    </Modal>
  );
};

// Create a wrapper component for the new interview context
const InterviewSettingsModalForNewInterview: React.FC<
  InterviewSettingsModalProps
> = (props) => {
  const { getValues, setValue } = useFormContext<NewInterviewFormInputs>();

  const initialValues: Partial<InterviewSettingsForm> = {
    isCamRecordingEnabled: getValues('interviewSettings.isCamRecordingEnabled'),
    isShuffleEnabled: getValues('interviewSettings.isShuffleEnabled'),
    isShowGuideEnabled: getValues('interviewSettings.isShowGuideEnabled'),
    isEmotionAnalysisEnabled: getValues(
      'interviewSettings.isEmotionAnalysisEnabled'
    ),
    isSuggestionEnabled: getValues('interviewSettings.isSuggestionEnabled'),
    isAiAvatarEnabled: getValues('interviewSettings.isAiAvatarEnabled'),
    title: getValues('title'),
    type: getValues('type'),
    isSecurityMeasuresEnabled: getValues(
      'interviewSettings.isSecurityMeasuresEnabled'
    ),
  };

  const handleSubmit = (data: InterviewSettingsForm) => {
    setValue(
      'interviewSettings.isCamRecordingEnabled',
      data.isCamRecordingEnabled
    );
    setValue('interviewSettings.isShuffleEnabled', data.isShuffleEnabled);
    setValue('interviewSettings.isShowGuideEnabled', data.isShowGuideEnabled);
    setValue(
      'interviewSettings.isEmotionAnalysisEnabled',
      data.isEmotionAnalysisEnabled
    );
    setValue('interviewSettings.isSuggestionEnabled', data.isSuggestionEnabled);
    setValue('interviewSettings.isAiAvatarEnabled', data.isAiAvatarEnabled);
    setValue(
      'interviewSettings.isSecurityMeasuresEnabled',
      data.isSecurityMeasuresEnabled
    );
    setValue('title', data.title);
    setValue('type', data.type);
  };

  return (
    <InterviewSettingsModal
      {...props}
      initialValues={initialValues}
      onSubmit={handleSubmit}
      isEditable
    />
  );
};

const Header: React.FC = () => {
  const { t } = useTranslation(['interview', 'common', 'candidate']);
  const InterviewSettingsClosure = useDisclosure();
  return (
    <HStack justifyContent="space-between" w="full" alignItems="start">
      <VStack align="flex-start">
        <Heading fontSize="2xl">
          {t('interview:createNewInterview.questionPreparation.stepTitle')}
        </Heading>
        <Text>
          {t('interview:createNewInterview.questionPreparation.description')}
        </Text>
      </VStack>
      <Button
        variant="outline"
        onClick={InterviewSettingsClosure.onToggle}
        px={2.5}
        py={5}
        size="sm"
        rounded="xl"
      >
        <IconSettings fontSize={20} />
      </Button>
      <InterviewSettingsModalForNewInterview
        isOpen={InterviewSettingsClosure.isOpen}
        onClose={InterviewSettingsClosure.onClose}
      />
    </HStack>
  );
};

type QuestionGenerationMode = 'manual' | 'ai';
type ButtonGroupSwitchProps = {
  type: QuestionGenerationMode;
  setType: (type: QuestionGenerationMode) => void;
};

const ButtonGroupSwitch: React.FC<ButtonGroupSwitchProps> = ({
  setType,
  type,
}) => {
  const { t } = useTranslation(['interview', 'common']);
  return (
    <ButtonGroup>
      <Button
        rightIcon={type === 'manual' ? <CheckboxRadio /> : <Stroke />}
        variant={type === 'manual' ? '@secondary' : 'outline'}
        onClick={() => setType('manual')}
      >
        {t(
          'interview:createNewInterview.questionPreparation.manualPreparation'
        )}
      </Button>
      <Button
        rightIcon={type === 'ai' ? <CheckboxRadio /> : <Stroke />}
        variant={type === 'ai' ? '@secondary' : 'outline'}
        onClick={() => setType('ai')}
      >
        <Text mr={1}>
          {t('interview:createNewInterview.questionPreparation.aiPreparation')}
        </Text>
        <Badge colorScheme="green">{t('common:new')}</Badge>
      </Button>
    </ButtonGroup>
  );
};

export const StepNewInterviewQuestions: React.FC<NewInterviewFormStepProps> = (
  props
) => {
  const { isActive } = props;
  const { control, getValues, watch } =
    useFormContext<NewInterviewFormInputs>();
  const { t } = useTranslation(['interview']);
  const [type, setType] = useState<QuestionGenerationMode>('manual');
  const formFieldArray = useFieldArray({
    control,
    name: 'questions',
  });
  const { append } = formFieldArray;

  if (!isActive) return null;

  return (
    <Box>
      <HStack justifyContent="space-between" alignItems="flex-start">
        <Header />
      </HStack>
      <Divider my={5} />
      <HStack alignItems="center">
        <ButtonGroupSwitch type={type} setType={setType} />
        {watch('type') === InterviewType.LanguageTest && (
          <LanguageSelectionMenu />
        )}
      </HStack>
      <Stack spacing="16px" my={5}>
        {type === 'ai' && (
          <AiQuestionsGenerate
            positionName={getValues('title')}
            formArray={formFieldArray}
          />
        )}
        <QuestionCardList formArray={formFieldArray} />
      </Stack>
      <Button
        variant="link"
        fontWeight="semibold"
        onClick={() => append(defaultQuestion)}
        color="brand.500"
      >
        <Icon icon={LuPlus} fontSize="lg" />
        {t('interview:createNewInterview.questionPreparation.addNewQuestion')}
      </Button>
    </Box>
  );
};
